import { useState } from "react";
import { api } from "@/App";
import { CheckCircle2, LifeBuoy, Loader2, Send } from "lucide-react";

const TOPICS = [["licence", "Licence"], ["activation", "Activation"], ["bug", "Bug"], ["billing", "Billing"], ["other", "Other"]];
const PLUGINS = [["general", "General / not sure"], ["denoise", "Denoise"], ["skin", "Skin"], ["texture", "Texture"], ["reactor", "Reactor"], ["genalpha", "Gen Alpha"], ["recast", "Recast"], ["rolloff", "Film Compression"]];
const EMPTY = { name: "", email: "", topic: "licence", plugin: "general", message: "", website: "" };
const field = "w-full bg-[#0e1013] border border-[#2a2f3d] px-3 py-2.5 text-sm text-white focus:border-white outline-none transition-colors";
const label = "block font-mono text-[11px] uppercase tracking-widest text-[#9ba3b4] space-y-1.5";

const errText = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return "Please check the form: " + d.map((x) => x?.loc?.slice(-1)[0]).filter(Boolean).join(", ") + ".";
  return "Couldn't send right now. Please try again in a moment.";
};

const Pick = ({ name, value, onChange, opts }) => (
  <select className={field} value={value} onChange={(e) => onChange(name, e.target.value)} data-testid={`support-${name}-select`}>
    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const Sent = ({ email, onAgain }) => (
  <div className="border border-[#a3ff4f]/60 bg-[#a3ff4f]/5 p-6 space-y-3" data-testid="support-success">
    <div className="flex items-center gap-2 font-display uppercase text-xl text-[#a3ff4f]"><CheckCircle2 size={20} /> Message sent</div>
    <p className="text-sm text-[#c9ced8]">Thanks! A confirmation is on its way to <b className="text-white">{email}</b>. We usually reply within 1–2 business days.</p>
    <button onClick={onAgain} className="btn-ghost px-4 py-2 text-xs" data-testid="support-send-another-btn">Send another message</button>
  </div>
);

export const SupportSection = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sent, setSent] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { await api.post("/support", f); setSent(f.email); setF(EMPTY); } catch (x) { setErr(errText(x)); }
    setBusy(false);
  };
  return (
    <section id="support" className="scroll-mt-6 card p-6 md:p-10 grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-10" data-testid="support-section">
      <div className="space-y-4">
        <div className="chip"><LifeBuoy size={12} /> support</div>
        <h2 className="font-display uppercase text-4xl md:text-5xl leading-[0.95]">Need a hand?</h2>
        <p className="text-sm md:text-base text-[#9ba3b4] max-w-md">Licence key not arriving, activation trouble, a bug in Resolve or a billing question: send us a message and a real colorist will get back to you.</p>
        <ul className="font-mono text-xs uppercase tracking-widest text-[#5c6479] space-y-2">
          <li>Reply within 1–2 business days</li>
          <li>Include your Resolve version and OS for bugs</li>
          <li>Never send your licence key in full</li>
        </ul>
        <div className="flex flex-wrap gap-2 pt-2">
          <a href="/activate#lost-key" className="btn-ghost px-4 py-2 text-xs" data-testid="support-lost-key-link">Lost your key?</a>
          <a href="/activate#machines" className="btn-ghost px-4 py-2 text-xs" data-testid="support-machines-link">Changed computer?</a>
        </div>
      </div>
      {sent ? <Sent email={sent} onAgain={() => setSent(null)} /> : (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="support-form">
          <label className={label}>Name<input className={field} required maxLength={120} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="support-name-input" /></label>
          <label className={label}>Email<input className={field} type="email" required maxLength={200} value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="support-email-input" /></label>
          <label className={label}>Topic<Pick name="topic" value={f.topic} onChange={set} opts={TOPICS} /></label>
          <label className={label}>Plugin<Pick name="plugin" value={f.plugin} onChange={set} opts={PLUGINS} /></label>
          <label className={`${label} md:col-span-2`}>Message
            <textarea className={`${field} min-h-[150px] resize-y`} required minLength={10} maxLength={5000} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="What happened? Which Resolve version and OS?" data-testid="support-message-input" />
          </label>
          <input type="text" tabIndex={-1} autoComplete="off" className="hidden" value={f.website} onChange={(e) => set("website", e.target.value)} aria-hidden />
          {err && <p className="md:col-span-2 text-xs text-[#ff453a]" data-testid="support-error">{err}</p>}
          <button disabled={busy} className="md:col-span-2 justify-self-start px-6 py-3 flex items-center gap-2 font-semibold uppercase bg-white text-black hover:bg-[#a3ff4f] transition-colors disabled:opacity-60" data-testid="support-submit-btn">
            {busy ? <Loader2 className="spin" size={16} /> : <Send size={16} />} Send message
          </button>
        </form>
      )}
    </section>
  );
};
